import { useEffect, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Modal } from '@/shared/ui/Modal';
import { Button } from '@/shared/ui/Button';
import type { ModoCaptura } from '@/features/captura/tipos';
import type {
  AbrirSessaoCaptura,
  EstadoSessaoCaptura,
  SessaoCaptura,
} from '@/features/captura/hooks/useSessaoCaptura';
import { formatarPlacaExibicao } from '@/features/captura/aplicarCamposCapturados';
import { IconAbrirFora, IconCopiar } from '@/features/captura/components/icones';
import '@/features/captura/captura.css';

type Props = {
  aberto: boolean;
  modo: ModoCaptura;
  /** Sessão (hook) de quem abriu o modal — a mesma recebe os campos/fotos. */
  captura: SessaoCaptura;
  /** Dicas para o cabeçalho do celular e O.S. a que as fotos pertencem. */
  parametros?: Omit<AbrirSessaoCaptura, 'modo'>;
  onFechar: () => void;
};

const TITULOS: Record<ModoCaptura, string> = {
  placa: 'Ler a placa pelo celular',
  fotos: 'Fotos do veículo pelo celular',
};

const horaFmt = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' });
const dataHoraFmt = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
});

function textoValidade(expiraEm: string): string | null {
  const d = new Date(expiraEm);
  if (Number.isNaN(d.getTime())) return null;
  const mesmoDia = d.toDateString() === new Date().toDateString();
  return `Código válido até ${mesmoDia ? horaFmt.format(d) : dataHoraFmt.format(d)}.`;
}

type Status = { classe: string; texto: string };

function statusDe(estado: EstadoSessaoCaptura, temSessao: boolean, celulares: number): Status {
  if (estado === 'erro') return { classe: 'captura-status--erro', texto: 'Código não gerado' };
  if (estado === 'expirada') return { classe: 'captura-status--expirada', texto: 'Código expirado' };
  if (!temSessao || estado === 'inativa') return { classe: '', texto: 'Gerando código…' };
  if (estado === 'conectando') return { classe: '', texto: 'Conectando…' };
  if (estado === 'sem-conexao') {
    return {
      classe: 'captura-status--erro',
      texto: 'Sem conexão com o servidor. Tentando de novo…',
    };
  }
  if (estado === 'celular-conectado') {
    return {
      classe: 'captura-status--conectado',
      texto: celulares > 1 ? `${celulares} celulares conectados ✓` : 'Celular conectado ✓',
    };
  }
  return { classe: 'captura-status--aguardando', texto: 'Aguardando o celular…' };
}

/**
 * QR code da sessão de captura. O celular lê, abre a página pública (sem
 * login) e manda placa/fotos; esta tela acompanha ao vivo pelo socket.
 * Mesmo componente no desktop e no web.
 */
export function ModalCapturaCelular({ aberto, modo, captura, parametros, onFechar }: Props) {
  const { sessao, estado, celulares, fotos, erro, ultimosCampos } = captura;
  const [copia, setCopia] = useState<'ok' | 'falhou' | null>(null);
  const [gerando, setGerando] = useState(false);
  const inputLinkRef = useRef<HTMLInputElement>(null);
  const timerCopiaRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Último pedido de abertura — lido só quando o modal abre, para não
  // recriar a sessão a cada render de quem passa um objeto literal.
  const parametrosRef = useRef(parametros);
  parametrosRef.current = parametros;
  const abrirRef = useRef(captura.abrir);
  abrirRef.current = captura.abrir;

  useEffect(() => {
    if (!aberto) return;
    setCopia(null);
    void abrirRef.current({ modo, ...(parametrosRef.current ?? {}) });
  }, [aberto, modo]);

  useEffect(
    () => () => {
      if (timerCopiaRef.current) clearTimeout(timerCopiaRef.current);
    },
    [],
  );

  if (!aberto) return null;

  const url = sessao?.urlCelular ?? '';
  const vencido = estado === 'expirada';
  const mostrarQr = Boolean(sessao) && estado !== 'erro';
  const status = statusDe(estado, Boolean(sessao), celulares);
  const placaRecebida = ultimosCampos?.placa ? formatarPlacaExibicao(ultimosCampos.placa) : null;
  const validade = sessao && !vencido ? textoValidade(sessao.expiraEm) : null;

  async function copiarLink() {
    if (!url) return;
    if (timerCopiaRef.current) clearTimeout(timerCopiaRef.current);
    try {
      await navigator.clipboard.writeText(url);
      setCopia('ok');
    } catch {
      // Sem permissão de área de transferência: deixa o link selecionado.
      inputLinkRef.current?.select();
      setCopia('falhou');
    }
    timerCopiaRef.current = setTimeout(() => setCopia(null), 3000);
  }

  async function gerarNovo() {
    setGerando(true);
    try {
      await captura.recriar();
    } finally {
      setGerando(false);
    }
  }

  async function tentarDeNovo() {
    setGerando(true);
    try {
      await captura.abrir({ modo, ...(parametros ?? {}) });
    } finally {
      setGerando(false);
    }
  }

  return (
    <Modal
      aberto
      titulo={TITULOS[modo]}
      onFechar={onFechar}
      tamanho="sm"
      rodape={<Button onClick={onFechar}>Fechar</Button>}
    >
      <div className="captura-qr">
        <p className="captura-qr__instrucao">
          {modo === 'placa'
            ? 'Aponte a câmera do celular para o código, fotografe a placa e confira os dados.'
            : 'Aponte a câmera do celular para o código e toque no desenho do veículo para fotografar cada parte.'}
        </p>

        {estado === 'erro' ? (
          <div className="alert alert--error captura-qr__erro" role="alert">
            <span>{erro ?? 'Não foi possível gerar o código.'}</span>
            <Button onClick={() => void tentarDeNovo()} disabled={gerando}>
              {gerando ? 'Gerando…' : 'Tentar de novo'}
            </Button>
          </div>
        ) : (
          <div
            className={`captura-qr__quadro${vencido ? ' captura-qr__quadro--vencido' : ''}`}
            aria-busy={!mostrarQr}
          >
            {mostrarQr ? (
              <QRCodeSVG
                value={url}
                size={220}
                level="M"
                marginSize={2}
                bgColor="#ffffff"
                fgColor="#000000"
                title="QR code para abrir a captura no celular"
                data-testid="captura-qr"
                data-valor={url}
              />
            ) : (
              <div className="captura-qr__esqueleto" aria-label="Gerando código…" />
            )}
            {vencido && (
              <div className="captura-qr__sobreposicao">
                <span>Este código expirou.</span>
                <Button variante="primary" onClick={() => void gerarNovo()} disabled={gerando}>
                  {gerando ? 'Gerando…' : 'Gerar novo código'}
                </Button>
              </div>
            )}
          </div>
        )}

        <span className={`captura-status ${status.classe}`} role="status" aria-live="polite">
          {status.texto}
        </span>

        {(placaRecebida || fotos.length > 0) && (
          <ul className="captura-qr__recebidos" aria-live="polite">
            {placaRecebida && (
              <li>
                Placa recebida: <span className="captura-qr__placa">{placaRecebida}</span>
              </li>
            )}
            {fotos.length > 0 && (
              <li>
                {fotos.length === 1 ? '1 foto recebida' : `${fotos.length} fotos recebidas`}
              </li>
            )}
          </ul>
        )}

        {mostrarQr && !vencido && (
          <>
            <div className="captura-qr__link">
              <input
                ref={inputLinkRef}
                className="input"
                value={url}
                readOnly
                aria-label="Link da captura pelo celular"
                onFocus={(e) => e.currentTarget.select()}
              />
              <Button onClick={() => void copiarLink()}>
                <IconCopiar />
                {copia === 'ok' ? 'Link copiado' : 'Copiar link'}
              </Button>
            </div>
            {copia === 'falhou' && (
              <span className="hint" role="status">
                Não deu para copiar automaticamente — o link está selecionado, use Ctrl+C.
              </span>
            )}
            <div className="captura-qr__acoes">
              <a className="btn" href={url} target="_blank" rel="noopener noreferrer">
                <IconAbrirFora />
                Abrir neste aparelho
              </a>
            </div>
          </>
        )}

        {validade && <p className="captura-qr__validade">{validade}</p>}
      </div>
    </Modal>
  );
}
